import { useCallback, useEffect, useState, useTransition } from "react"
import type { DirectoryTreeState, UseDirectoryTreeStateProps } from "./types"

/**
 * A React hook for managing the state of a directory tree.
 * ディレクトリツリーの状態を管理するための React フック。
 *
 * This hook is designed to work with Server-Side Rendering (SSR) by allowing
 * the initial expansion state to be passed in as a prop.
 * このフックは、初期の展開状態をプロパティとして渡すことで、
 * サーバーサイドレンダリング (SSR) と連携するように設計されています。
 *
 * @param props - The properties for the hook.
 * @returns The state and actions for the directory tree.
 */
export const useDirectoryTreeState = ({
    initialExpanded = new Set<string>(),
    storageKey
}: UseDirectoryTreeStateProps = {}): DirectoryTreeState => {
    const [isPending, startTransition] = useTransition()
    const [expanded, setExpanded] = useState(() => {
        if (typeof window !== "undefined" && storageKey) {
            try {
                const item = window.localStorage.getItem(storageKey)
                if (item) {
                    const parsedItem = JSON.parse(item)
                    return new Set<string>(parsedItem)
                }
            } catch (error) {
                console.error("[useDirectoryTreeState] Error reading from localStorage", error)
            }
        }
        return initialExpanded
    })

    useEffect(() => {
        if (storageKey && typeof window !== "undefined") {
            try {
                window.localStorage.setItem(storageKey, JSON.stringify(Array.from(expanded)))
            } catch (error) {
                console.warn(`Error setting localStorage key "${storageKey}":`, error)
            }
        }
    }, [expanded, storageKey])

    const toggle = useCallback((path: string) => {
        startTransition(() => {
            setExpanded((prev) => {
                const next = new Set(prev)
                if (next.has(path)) {
                    next.delete(path)
                } else {
                    next.add(path)
                }
                return next
            })
        })
    }, [])

    const expand = useCallback((path: string) => {
        startTransition(() => {
            setExpanded((prev) => {
                const next = new Set(prev)
                if (!next.has(path)) {
                    next.add(path)
                }
                return next
            })
        })
    }, [])

    const collapse = useCallback((path: string) => {
        startTransition(() => {
            setExpanded((prev) => {
                const next = new Set(prev)
                if (next.has(path)) {
                    next.delete(path)
                }
                return next
            })
        })
    }, [])

    const collapseMultiple = useCallback((paths: string[]) => {
        startTransition(() => {
            setExpanded((prev) => {
                const next = new Set(prev)
                for (const path of paths) {
                    if (next.has(path)) {
                        next.delete(path)
                    }
                }
                return next
            })
        })
    }, [])

    const expandMultiple = useCallback((paths: string[]) => {
        startTransition(() => {
            setExpanded((prev) => {
                const next = new Set(prev)
                for (const path of paths) {
                    if (!next.has(path)) {
                        next.add(path)
                    }
                }
                return next
            })
        })
    }, [])

    const collapseAll = useCallback(() => {
        startTransition(() => {
            setExpanded(new Set())
        })
    }, [])

    const isExpanded = useCallback((path: string) => expanded.has(path), [expanded])

    return {
        expanded,
        toggle,
        expand,
        collapse,
        collapseMultiple,
        expandMultiple,
        collapseAll,
        isExpanded,
        isPending,
    }
}
